/**
 * Result modals shown at the end of a game: WonScreen, LostScreen, ExhaustedScreen.
 * All three reuse the shared <Modal /> wrapper from `parts.jsx`.
 */
import { useEffect, useState } from "react";
import { Trophy, AlertTriangle, LogOut, RefreshCw, ListChecks, Crown, Home, Bot, Share2, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal, ActivitySummary, formatTime } from "./parts";

import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// Pick the correct plural key for "wvl_keep_going" based on `left` (games)
// and `need` (wins) so PT/EN/ES don't render "1 jogos" / "1 vitórias".
const keepGoingKey = (left, need) => {
  if (left === 1 && need === 1) return "wvl_keep_going_1g_1v";
  if (left === 1) return "wvl_keep_going_1g_nv";
  if (need === 1) return "wvl_keep_going_ng_1v";
  return "wvl_keep_going";
};

/* Inline ranking shown at the END of a training/competition match — replaces
   the standalone "Resultados" page (its home button was removed). */
function FinalRanking({ gameId, mode, playerName }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!gameId || !mode) return;
    const path = mode === "competition"
      ? `/rankings/competition/${gameId}`
      : `/rankings/training/${gameId}`;
    api.get(path, { params: { top: 10 } })
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [gameId, mode]);
  if (err) return null;
  if (!data) return (
    <div className="mt-4 text-xs text-slate-400 text-center" data-testid="ranking-loading">{t("loading_ranking")}</div>
  );
  const rows = data.rows || data.results || [];
  if (!rows.length) return null;
  const norm = (s) => String(s || "").trim().toLowerCase();
  const me = norm(playerName);
  return (
    <div className="mt-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-3 text-left" data-testid="ranking-final">
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="w-4 h-4 text-amber-500" />
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">
          Top {Math.min(rows.length, 10)} · {mode === "competition" ? t("competition") : t("training")}
        </div>
      </div>
      <ol className="space-y-1 max-h-48 overflow-y-auto" data-testid="ranking-final-list">
        {rows.slice(0, 10).map((r, i) => {
          const rowName = r.name || r.player_name || "—";
          const isMe = me && norm(rowName) === me;
          return (
            <li
              key={`${rowName}-${r.time_seconds ?? r.score ?? i}`}
              className={`flex items-center justify-between text-xs px-2 py-1 rounded ${isMe ? "bg-emerald-100 border border-emerald-300 font-bold" : ""}`}
              data-testid={`ranking-final-row-${i + 1}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-5 inline-block text-center font-mono ${isMe ? "text-emerald-700" : "text-slate-500"}`}>#{i + 1}</span>
                <span className="truncate">{rowName}</span>
                {r.school && <span className="text-slate-400 truncate hidden sm:inline">· {r.school}</span>}
              </div>
              {r.time != null && (
                <span className="font-mono text-slate-700 shrink-0 ml-2">{formatTime(Number(r.time))}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* Identity card shown on a PASSED eliminatória window: student name, school
   (when present), game and tier/level. Rendered for every eliminatória type
   so a superada knockout always shows who passed it. */
function ElimPlayerInfo({ info }) {
  const { t } = useI18n();
  if (!info) return null;
  const rows = [
    { k: "student", label: t("elim_info_student"), value: info.name },
    { k: "school", label: t("elim_info_school"), value: info.school },
    { k: "game", label: t("elim_info_game"), value: info.game },
    { k: "level", label: t("elim_info_level"), value: info.level },
  ].filter((r) => r.value != null && String(r.value).trim() !== "");
  if (!rows.length) return null;
  return (
    <div className="mt-3 rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 py-3 text-left" data-testid="elim-player-info">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{t("elim_info_title")}</div>
      <dl className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.k} className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] font-semibold text-slate-500 shrink-0">{r.label}</dt>
            <dd className="text-sm font-bold text-slate-800 text-right break-words" data-testid={`elim-info-${r.k}`}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function WonScreen({
  mode, time, finishData, history, busy, credentialId,
  onPlayAgain, onChooseOtherGame, onExit, onContinueExhausted,
  vsRobot, // { robotName, myRank: {rank,total_players,wins,losses,ties}, onShare }
  gameId, playerName, elimInfo,
}) {
  const { t } = useI18n();
  const noAttempts = mode === "competition" && (finishData?.attempts_available ?? 1) <= 0;

  // ---- VS ROBOT branch --------------------------------------------------
  if (mode === "vs_robot") {
    const ms = vsRobot?.mentalStrength ?? 5;
    const delta = typeof vsRobot?.pointsDelta === "number" ? vsRobot.pointsDelta : 2 * ms;
    const totalPts = vsRobot?.myRank?.points;
    const isElim = !!vsRobot?.elim;
    const elimPassed = !!vsRobot?.elim?.passed;
    return (
      <Modal>
        <Trophy className="w-14 h-14 text-amber-500 mx-auto mb-3" />
        <h2
          className="text-3xl md:text-4xl font-[Fredoka] font-extrabold text-emerald-600 mb-1 tracking-wide"
          data-testid="game-won-title"
        >
          {t("defeated_robot")}
        </h2>
        {vsRobot?.robotName && (
          <p className="text-slate-700 text-sm md:text-base">
            {t("beat_robot_duel", { robot: vsRobot.robotName })}
          </p>
        )}
        {elimPassed && (
          <div className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-3 border-2 border-emerald-300 shadow-md" data-testid="vsr-elim-passed">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-100">{t("knockout_label")}</div>
            <div className="text-xl font-[Fredoka] font-extrabold leading-tight">{t("passed_uppercase")}</div>
            <div className="text-xs text-emerald-50/95 mt-0.5">{t("knockout_now_complete")}</div>
          </div>
        )}
        {isElim && elimPassed && <ElimPlayerInfo info={elimInfo} />}
        {/* Points breakdown card — only for ranking mode (not when playing an eliminatória). */}
        {!isElim && (
          <div className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-violet-50 border-2 border-emerald-200 p-3" data-testid="vsr-points-card">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">{t("you_won_points")}</div>
            <div className="text-3xl font-[Fredoka] font-extrabold text-emerald-600" data-testid="vsr-points-delta">+{Math.round(delta * 10) / 10} {t("points_unit")}</div>
            {typeof totalPts === "number" && (
              <div className="mt-2 pt-2 border-t border-emerald-200 text-xs text-slate-700">
                {t("total_points")} <b className="text-violet-700 text-base font-[Fredoka]" data-testid="vsr-points-total">{Math.round(totalPts * 10) / 10}</b>
              </div>
            )}
          </div>
        )}
        {!isElim && (vsRobot?.myRank ? (
          <p className="text-slate-700 text-sm md:text-base mt-2">
            #<b className="text-amber-600">{vsRobot.myRank.rank}</b> / {vsRobot.myRank.total_players} ·{" "}
            <span className="text-emerald-700 font-bold">{vsRobot.myRank.wins}V</span> ·{" "}
            <span className="text-rose-600 font-bold">{vsRobot.myRank.losses}D</span>
          </p>
        ) : (
          <p className="text-slate-400 text-xs mt-2">{t("calculating_ranking")}</p>
        ))}
        <div className="flex flex-col gap-2 mt-4">
          {!isElim && vsRobot?.myRank && vsRobot.onShare && (
            <Button
              data-testid="vsr-share-rank"
              onClick={vsRobot.onShare}
              className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white rounded-full font-bold shadow-lg"
            >
              <Share2 className="w-4 h-4 mr-2" /> {t("share_my_rank", { n: vsRobot.myRank.rank })}
            </Button>
          )}
          {!elimPassed && (
            <Button data-testid="btn-play-again" onClick={onPlayAgain} disabled={busy} className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold">
              <RefreshCw className="w-4 h-4 mr-2" /> {t("another_duel")}
            </Button>
          )}
          <Button data-testid="btn-other-game" onClick={onChooseOtherGame} variant="outline" className="rounded-full font-bold">
            {elimPassed ? (
              <><Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}</>
            ) : (
              <><Bot className="w-4 h-4 mr-2" /> {t("choose_other_robot")}</>
            )}
          </Button>
          <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  // ---- VS HUMANO branch ---------------------------------------------
  if (mode === "vs_human") {
    if (vsRobot?.abandoned) {
      return (
        <Modal>
          <AlertTriangle className="w-14 h-14 text-slate-400 mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-slate-600 mb-1 tracking-wide" data-testid="game-abandoned-title">
            {t("duel_discarded")}
          </h2>
          <p className="text-slate-700 text-sm md:text-base">{t("opponent_disconnected")}</p>
          <p className="text-slate-500 text-xs mt-2">{t("tie_no_record")}</p>
          <div className="flex flex-col gap-2 mt-4">
            <Button data-testid="btn-other-game" onClick={onChooseOtherGame} className="bg-violet-500 hover:bg-violet-600 text-white rounded-full font-bold">
              <RefreshCw className="w-4 h-4 mr-2" /> {t("back_to_lobby")}
            </Button>
            <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }
    return (
      <Modal>
        <Trophy className="w-14 h-14 text-amber-500 mx-auto mb-3" />
        <h2 className="text-3xl md:text-4xl font-[Fredoka] font-extrabold text-violet-600 mb-1 tracking-wide" data-testid="game-won-title">
          {t("won_duel")}
        </h2>
        {vsRobot?.robotName && (
          <p className="text-slate-700 text-sm md:text-base">{t("beat_in_realtime", { robot: vsRobot.robotName })}</p>
        )}
        <p className="text-slate-500 text-xs mt-2">{t("want_more_lobby")}</p>
        <div className="flex flex-col gap-2 mt-4">
          {/* Winner sees a friendly "play again" — NOT a "revenge" — because
              they already won. The underlying flow is the same as the loser's
              Desforra button. */}
          {vsRobot?.onRematch && vsRobot?.opponentUserId && (
            <Button
              data-testid="btn-vsh-play-again"
              onClick={() => vsRobot.onRematch()}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("play_again_with", { name: vsRobot.robotName || t("opponent_default") })}
            </Button>
          )}
          <Button data-testid="btn-other-game" onClick={onChooseOtherGame} className="bg-violet-500 hover:bg-violet-600 text-white rounded-full font-bold">
            <RefreshCw className="w-4 h-4 mr-2" /> {t("another_duel")}
          </Button>
          <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  // ---- Eliminatórias branch ---------------------------------------------
  if (mode === "eliminatoria") {
    const passed = !!finishData?.passed;
    const isFinalist = !!finishData?.is_new_finalist;
    const target = finishData?.target_time;
    const attemptsLeft = finishData?.attempts_left ?? 0;
    const elimMode = finishData?.elim_mode || "target_time";

    // ---- Vitórias vs Derrotas — same "you WON this game" branch, but no
    // ---- time-alvo shaming and instead show the running scoreboard.
    if (elimMode === "wins_vs_losses") {
      // Prefer group-aware payload (`wvl_progress`): wins/losses reflect
      // the CURRENT attempt (not the whole eliminatória).
      const wvl = finishData?.wvl_progress || null;
      const winsCount = wvl?.wins_in_attempt ?? finishData?.wins_count ?? 0;
      const lossesCount = wvl?.losses_in_attempt ?? finishData?.losses_count ?? 0;
      const winsRequired = wvl?.wins_required ?? finishData?.wins_required ?? 3;
      const totalGames = wvl?.total_games ?? finishData?.total_games ?? 5;
      const attemptsLeftWvl = Number(wvl?.attempts_left ?? attemptsLeft ?? 0);
      const gamesPlayed = winsCount + lossesCount;
      const gamesLeft = Math.max(0, totalGames - gamesPlayed);
      // The current attempt can still be CONTINUED (open group with sub-games
      // left) independently of how many whole ATTEMPTS remain — the backend
      // only consumes an attempt when a NEW group is opened. So "play next"
      // must show whenever the open attempt has games left, even if
      // attempts_left === 0 (e.g. single-attempt eliminatórias).
      const currentAttemptFailed = !!wvl?.current_attempt_failed;
      const gamesLeftInAttempt = Number(wvl?.games_left_in_attempt ?? gamesLeft);
      const canContinueAttempt = !passed && !currentAttemptFailed && gamesLeftInAttempt > 0;
      const canStartNewAttempt = !passed && (currentAttemptFailed || gamesLeftInAttempt === 0) && attemptsLeftWvl > 0;
      return (
        <Modal>
          {isFinalist ? (
            <Crown className="w-14 h-14 text-amber-400 mx-auto mb-3 drop-shadow" />
          ) : (
            <Trophy className={`w-14 h-14 mx-auto mb-3 ${passed ? "text-emerald-500" : "text-amber-500"}`} />
          )}
          <h2 className={`text-2xl md:text-3xl font-[Fredoka] font-extrabold mb-1 tracking-wide ${
            isFinalist ? "text-amber-500" :
            passed ? "text-emerald-600" : "text-emerald-600"
          }`} data-testid="game-won-title">
            {isFinalist ? t("you_are_finalist") : passed ? t("knockout_passed") : t("wvl_won_this_game")}
          </h2>
          {isFinalist && (
            <p className="text-amber-700 text-sm md:text-base font-bold mb-2">
              {t("qualified_for_final")}
            </p>
          )}
          {/* Scoreboard */}
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="wvl-scoreboard">
            <div className="flex flex-col items-center bg-emerald-50 border border-emerald-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">{t("wvl_wins")}</span>
              <span className="font-[Fredoka] text-2xl font-bold text-emerald-700" data-testid="wvl-wins-count">
                {winsCount}
              </span>
            </div>
            <div className="flex flex-col items-center bg-rose-50 border border-rose-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wide">{t("wvl_losses")}</span>
              <span className="font-[Fredoka] text-2xl font-bold text-rose-700" data-testid="wvl-losses-count">
                {lossesCount}
              </span>
            </div>
          </div>
          {(passed || isFinalist) && <ElimPlayerInfo info={elimInfo} />}
          {canContinueAttempt && (
            <p className="text-slate-600 text-xs md:text-sm mt-3">
              {(() => {
                const need = Math.max(0, winsRequired - winsCount);
                return t(keepGoingKey(gamesLeftInAttempt, need), { left: gamesLeftInAttempt, need });
              })()}
            </p>
          )}
          {currentAttemptFailed && !passed && (
            <p className="text-amber-700 text-xs md:text-sm mt-3 font-semibold">
              {attemptsLeftWvl > 0
                ? t("wvl_attempt_failed_hint", { left: attemptsLeftWvl, plural: attemptsLeftWvl === 1 ? "" : "s" })
                : t("wvl_no_attempts_left")}
            </p>
          )}
          <div className="flex flex-col gap-2 mt-4">
            {(canContinueAttempt || canStartNewAttempt) && (
              <Button
                data-testid="btn-elim-retry"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> {canContinueAttempt ? t("wvl_play_next") : t("wvl_start_new_attempt")}
              </Button>
            )}
            <Button
              data-testid="btn-elim-back-lobby"
              onClick={onChooseOtherGame}
              className={`rounded-full font-bold ${
                isFinalist ? "bg-amber-500 hover:bg-amber-600 text-white" :
                passed ? "bg-violet-600 hover:bg-violet-700 text-white" : ""
              }`}
              variant={(isFinalist || passed) ? "default" : "outline"}
            >
              <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
            </Button>
            <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }

    // ---- AVG-TIME (v2, 2026-02) — the player has `max_attempts` attempts;
    // ---- each attempt is a run of `total_games` sub-games. All sub-games
    // ---- must be WON in an attempt and their average time must be ≤ target.
    // ---- Losing a sub-game or finishing the attempt too slow fails ONLY
    // ---- that attempt — the player can start a fresh one if attempts_left
    // ---- > 0.
    if (elimMode === "avg_time") {
      const avg = finishData?.avg_progress || {};
      const times = Array.isArray(avg.current_attempt_times) ? avg.current_attempt_times : [];
      const targetAvg = Number(avg.target_avg || 0);
      const currentAvg = avg.current_attempt_avg != null ? Number(avg.current_attempt_avg) : null;
      const attemptNumber = Number(avg.attempt_number || 1);
      const attemptsUsed = Number(avg.attempts_used || 0);
      const attemptsLeftAvg = Number(avg.attempts_left || 0);
      const maxAttempts = Number(avg.max_attempts || 3);
      const totalGames = Number(avg.total_games || 5);
      const gamesPlayedInAttempt = Number(avg.games_played_in_attempt || 0);
      const gamesLeftInAttempt = Number(avg.games_left_in_attempt || 0);
      const nextMax = Number(avg.max_time_next_game_fair || 0);
      const attemptFailed = !!avg.current_attempt_failed;
      const failReason = avg.current_attempt_failure_reason;
      const canRetryAttempt = attemptFailed && attemptsLeftAvg > 0;
      const stillPlayingSameAttempt = !passed && !attemptFailed && gamesLeftInAttempt > 0;
      return (
        <Modal>
          {isFinalist ? (
            <Crown className="w-14 h-14 text-amber-400 mx-auto mb-3 drop-shadow" />
          ) : passed ? (
            <Trophy className="w-14 h-14 mx-auto mb-3 text-emerald-500" />
          ) : attemptFailed ? (
            <AlertTriangle className="w-14 h-14 mx-auto mb-3 text-rose-500" />
          ) : (
            <Timer className="w-14 h-14 mx-auto mb-3 text-sky-500" />
          )}
          <h2 className={`text-2xl md:text-3xl font-[Fredoka] font-extrabold mb-1 tracking-wide ${
            isFinalist ? "text-amber-500" :
            passed ? "text-emerald-600" :
            attemptFailed ? "text-rose-600" : "text-sky-700"
          }`} data-testid="game-won-title">
            {isFinalist ? t("you_are_finalist")
              : passed ? t("knockout_passed")
              : attemptFailed
                ? (failReason === "lost_game" ? t("avg_time_attempt_lost_title")
                    : failReason === "avg_unreachable" ? t("avg_time_attempt_unreachable_title")
                    : t("avg_time_attempt_slow_title"))
                : t("avg_time_progress_title")}
          </h2>
          <p className="text-slate-600 text-xs md:text-sm mt-1" data-testid="avg-attempt-header">
            {t("avg_time_attempt_x_of_y", { current: attemptNumber, total: maxAttempts })}
          </p>
          {/* Scoreboard for avg_time */}
          <div className="mt-2 grid grid-cols-3 gap-2" data-testid="avg-scoreboard">
            <div className="flex flex-col items-center bg-sky-50 border border-sky-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wide">{t("avg_time_games_col")}</span>
              <span className="font-[Fredoka] text-xl font-bold text-sky-700" data-testid="avg-attempts">
                {gamesPlayedInAttempt}<span className="text-sky-500/60 text-base"> / {totalGames}</span>
              </span>
            </div>
            <div className={`flex flex-col items-center rounded-xl py-1.5 border ${
              currentAvg != null && currentAvg <= targetAvg ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
            }`}>
              <span className={`text-[10px] font-bold uppercase tracking-wide ${
                currentAvg != null && currentAvg <= targetAvg ? "text-emerald-700" : "text-amber-700"
              }`}>{t("avg_time_current")}</span>
              <span className="font-[Fredoka] text-xl font-bold font-mono" data-testid="avg-current">
                {currentAvg != null ? currentAvg.toFixed(1) + "s" : "—"}
              </span>
            </div>
            <div className="flex flex-col items-center bg-violet-50 border border-violet-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wide">{t("avg_time_target")}</span>
              <span className="font-[Fredoka] text-xl font-bold text-violet-700 font-mono" data-testid="avg-target">
                {targetAvg.toFixed(0)}s
              </span>
            </div>
          </div>
          {/* Times list of the current attempt */}
          {times.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="avg-times-list">
              {times.map((s, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-mono font-bold">
                  #{i + 1}: {Number(s).toFixed(1)}s
                </span>
              ))}
            </div>
          )}
          {(passed || isFinalist) && <ElimPlayerInfo info={elimInfo} />}
          {/* Guidance for continuing THIS attempt */}
          {stillPlayingSameAttempt && (
            <p className="text-slate-700 text-xs md:text-sm mt-3" data-testid="avg-next-needed">
              {t("avg_time_next_game_hint", { seconds: nextMax.toFixed(1) })}
            </p>
          )}
          {/* Warning banners on failed attempt */}
          {attemptFailed && (
            <div className="text-rose-700 text-xs md:text-sm mt-3 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="avg-attempt-failed">
              <b>
                {failReason === "lost_game" ? t("avg_time_attempt_lost_hint")
                  : failReason === "avg_unreachable" ? t("avg_time_attempt_unreachable_hint")
                  : t("avg_time_attempt_slow_hint")}
              </b>
              <br />
              {canRetryAttempt
                ? t("avg_time_attempts_left_hint", { left: attemptsLeftAvg, plural: attemptsLeftAvg === 1 ? "" : "s" })
                : t("avg_time_no_attempts_left")}
            </div>
          )}
          <div className="flex flex-col gap-2 mt-4">
            {/* CONTINUE current attempt (won this sub-game, more sub-games remain) */}
            {stillPlayingSameAttempt && (
              <Button
                data-testid="btn-elim-retry"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("avg_time_play_next_in_attempt", { current: gamesPlayedInAttempt + 1, total: totalGames })}
              </Button>
            )}
            {/* START a fresh attempt after a failure */}
            {canRetryAttempt && (
              <Button
                data-testid="btn-elim-new-attempt"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> {t("avg_time_start_new_attempt")}
              </Button>
            )}
            <Button
              data-testid="btn-elim-back-lobby"
              onClick={onChooseOtherGame}
              className={`rounded-full font-bold ${
                isFinalist ? "bg-amber-500 hover:bg-amber-600 text-white" :
                passed ? "bg-violet-600 hover:bg-violet-700 text-white" : ""
              }`}
              variant={(isFinalist || passed) ? "default" : "outline"}
            >
              <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
            </Button>
            <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }

    return (
      <Modal>
        {isFinalist ? (
          <Crown className="w-14 h-14 text-amber-400 mx-auto mb-3 drop-shadow" />
        ) : (
          <Trophy className={`w-14 h-14 mx-auto mb-3 ${passed ? "text-emerald-500" : "text-amber-500"}`} />
        )}
        <h2 className={`text-2xl md:text-3xl font-[Fredoka] font-extrabold mb-1 tracking-wide ${
          isFinalist ? "text-amber-500" :
          passed ? "text-emerald-600" : "text-amber-600"
        }`} data-testid="game-won-title">
          {isFinalist ? t("you_are_finalist") : passed ? t("knockout_passed") : t("almost")}
        </h2>
        {isFinalist && (
          <p className="text-amber-700 text-sm md:text-base font-bold mb-2">
            {t("qualified_for_final")}
          </p>
        )}
        <p className="text-slate-700 text-sm md:text-base mb-1">
          {t("time_label")} <span className="font-mono font-bold text-slate-900">{formatTime(time)}</span>
          {target ? <span className="text-slate-500"> · {t("target_label")} {Number(target).toFixed(0)}s</span> : null}
        </p>
        {(passed || isFinalist) && <ElimPlayerInfo info={elimInfo} />}
        {!passed && (
          <p className="text-amber-700 text-xs md:text-sm">
            {t("won_but_not_target")} {t("attempts_left")}{" "}
            <b data-testid="elim-attempts-left">{attemptsLeft}</b>
          </p>
        )}
        <div className="flex flex-col gap-2 mt-4">
          {!passed && attemptsLeft > 0 && (
            <Button
              data-testid="btn-elim-retry"
              onClick={onPlayAgain}
              disabled={busy}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> {t("try_again")}
            </Button>
          )}
          <Button
            data-testid="btn-elim-back-lobby"
            onClick={onChooseOtherGame}
            className={`rounded-full font-bold ${
              isFinalist ? "bg-amber-500 hover:bg-amber-600 text-white" :
              passed ? "bg-violet-600 hover:bg-violet-700 text-white" : ""
            }`}
            variant={(isFinalist || passed) ? "default" : "outline"}
          >
            <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
          </Button>
          <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  // ---- Default (training / competition) ---------------------------------
  return (
    <Modal>
      <Trophy className="w-14 h-14 text-amber-500 mx-auto mb-3" />
      <h2
        className="text-3xl md:text-4xl font-[Fredoka] font-extrabold text-emerald-600 mb-1 tracking-wide"
        data-testid="game-won-title"
      >
        {mode === "competition" ? t("victory") : t("you_won_default")}
      </h2>
      <p className="text-slate-700 text-sm md:text-base mb-2">
        {t("time_label")} <span className="font-mono font-bold text-slate-900">{formatTime(time)}</span>
      </p>
      {mode === "competition" && finishData && (
        <p className="text-slate-700 text-xs md:text-sm">
          {t("attempts_left")}{" "}
          <span className="font-bold text-slate-900" data-testid="attempts-left">
            {finishData.attempts_available ?? "—"}
          </span>
        </p>
      )}
      {mode === "competition" && history && <ActivitySummary history={history} />}
      {(mode === "training" || mode === "competition") && (
        <FinalRanking gameId={gameId} mode={mode} playerName={playerName} />
      )}
      <div className="flex flex-col gap-2 mt-4">
        {noAttempts && onContinueExhausted ? (
          // Competition — final attempt done. Show a CONTINUAR button so the
          // player can read this modal at their own pace before jumping to
          // the "Tentativas esgotadas" farewell screen.
          <Button
            data-testid="btn-continue-exhausted"
            onClick={onContinueExhausted}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold"
          >
            {t("continue_btn")}
          </Button>
        ) : (
          <Button
            data-testid="btn-play-again"
            onClick={onPlayAgain}
            disabled={busy || noAttempts}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> {t("play_again")}
          </Button>
        )}
        {mode !== "competition" && (
          <Button
            data-testid="btn-other-game"
            onClick={onChooseOtherGame}
            variant="outline"
            className="rounded-full font-bold"
          >
            <ListChecks className="w-4 h-4 mr-2" /> {t("choose_other_game")}
          </Button>
        )}
        {!(noAttempts && onContinueExhausted) && (
          <Button
            data-testid="btn-back-home"
            onClick={onExit}
            variant="ghost"
            className="rounded-full text-slate-500"
          >
            {t("exit_lower")}
          </Button>
        )}
      </div>
    </Modal>
  );
}


export function LostScreen({
  mode, finishData, history, busy,
  onPlayAgain, onChooseOtherGame, onExit, onContinueExhausted,
  vsRobot, // { robotName }
  gameId, playerName,
}) {
  const { t } = useI18n();
  const noAttempts = mode === "competition" && (finishData?.attempts_available ?? 1) <= 0;

  if (mode === "vs_human") {
    if (vsRobot?.abandoned) {
      return (
        <Modal>
          <AlertTriangle className="w-14 h-14 text-slate-400 mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-slate-600 mb-1 tracking-wide" data-testid="game-abandoned-title">
            {t("duel_discarded")}
          </h2>
          <p className="text-slate-700 text-sm md:text-base">{t("lost_connection_15s")}</p>
          <p className="text-slate-500 text-xs mt-2">{t("tie_not_saved")}</p>
          <div className="flex flex-col gap-2 mt-4">
            <Button data-testid="btn-other-game" onClick={onChooseOtherGame} className="bg-violet-500 hover:bg-violet-600 text-white rounded-full font-bold">
              <RefreshCw className="w-4 h-4 mr-2" /> {t("back_to_lobby")}
            </Button>
            <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }
    return (
      <Modal>
        <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
        <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide" data-testid="game-lost-title">
          {t("lost_duel")}
        </h2>
        {vsRobot?.robotName && (
          <p className="text-slate-700 text-sm md:text-base">{t("robot_finished_first", { robot: vsRobot.robotName })}</p>
        )}
        <p className="text-slate-500 text-xs mt-2">{t("try_again_lobby")}</p>
        <div className="flex flex-col gap-2 mt-4">
          {vsRobot?.onRematch && vsRobot?.opponentUserId && (
            <Button
              data-testid="btn-vsh-rematch"
              onClick={() => vsRobot.onRematch()}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("rematch", { name: vsRobot.robotName || t("rematch_short") })}
            </Button>
          )}
          <Button data-testid="btn-other-game" onClick={onChooseOtherGame} className="bg-violet-500 hover:bg-violet-600 text-white rounded-full font-bold">
            <RefreshCw className="w-4 h-4 mr-2" /> {t("back_to_lobby")}
          </Button>
          <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  if (mode === "vs_robot") {
    const ms = vsRobot?.mentalStrength ?? 5;
    const delta = typeof vsRobot?.pointsDelta === "number" ? vsRobot.pointsDelta : -0.5 * ms;
    const totalPts = vsRobot?.myRank?.points;
    const isElim = !!vsRobot?.elim;
    // In Eliminatórias mode the backend returns the CURRENT remaining
    // attempts in `finishData.attempts_left`. Surface it on screen so the
    // player always knows exactly how many retries are left (user request
    // Feb-2026: "informar relativamente ao número de tentativas que ainda
    // estão disponíveis").
    const elimLoaded = typeof finishData?.attempts_left === "number";
    const elimAttemptsLeft = Number(finishData?.attempts_left ?? 0);
    const hasAttemptsLeft = elimAttemptsLeft > 0;
    return (
      <Modal>
        <Bot className="w-14 h-14 text-rose-500 mx-auto mb-3" />
        <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide" data-testid="game-lost-title">
          {t("robot_won")}
        </h2>
        {vsRobot?.robotName && (
          <p className="text-slate-700 text-sm md:text-base">
            {t("robot_finished_first", { robot: vsRobot.robotName })}
          </p>
        )}
        {isElim && (
          <div className="mt-3 rounded-2xl bg-amber-50 border-2 border-amber-200 p-3" data-testid="vsr-elim-failed">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-800">{t("knockout_label")}</div>
            <div className="text-base font-[Fredoka] font-extrabold text-amber-900 leading-tight">{t("failed_attempt")}</div>
            <div className="text-xs text-amber-800/90 mt-0.5">{t("each_new_attempt_costs")}</div>
            <div
              className="mt-2 text-xs font-bold text-amber-900"
              data-testid="vsr-elim-attempts-left"
            >
              {!elimLoaded
                ? t("checking_attempts")
                : hasAttemptsLeft
                ? (elimAttemptsLeft === 1 ? t("attempts_left_one") : t("attempts_left_many", { left: elimAttemptsLeft }))
                : t("no_attempts_left_sentence")}
            </div>
          </div>
        )}
        {/* Points breakdown card — only for ranking mode (not when playing an eliminatória). */}
        {!isElim && (
          <div className="mt-3 rounded-2xl bg-gradient-to-br from-rose-50 to-slate-50 border-2 border-rose-200 p-3" data-testid="vsr-points-card">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">{t("you_lost_points")}</div>
            <div className="text-3xl font-[Fredoka] font-extrabold text-rose-600" data-testid="vsr-points-delta">{Math.round(delta * 10) / 10} {t("points_unit")}</div>
            {typeof totalPts === "number" && (
              <div className="mt-2 pt-2 border-t border-rose-200 text-xs text-slate-700">
                {t("total_points")} <b className={`text-base font-[Fredoka] ${totalPts >= 0 ? "text-violet-700" : "text-rose-700"}`} data-testid="vsr-points-total">{Math.round(totalPts * 10) / 10}</b>
              </div>
            )}
          </div>
        )}
        <p className="text-slate-500 text-xs mt-2">
          {isElim
            ? (!elimLoaded
                ? t("checking_attempts")
                : hasAttemptsLeft
                ? (elimAttemptsLeft === 1 ? t("try_again_one_attempt") : t("try_again_many_attempts", { left: elimAttemptsLeft }))
                : t("no_attempts_left_sentence"))
            : t("dont_give_up")}
        </p>
        <div className="flex flex-col gap-2 mt-4">
          {(!isElim || !elimLoaded || hasAttemptsLeft) && (
            <Button data-testid="btn-try-again" onClick={onPlayAgain} disabled={busy || (isElim && !elimLoaded)} className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold">
              <RefreshCw className="w-4 h-4 mr-2" /> {isElim ? t("new_attempt") : t("another_duel")}
            </Button>
          )}
          <Button data-testid="btn-other-game" onClick={onChooseOtherGame} variant="outline" className="rounded-full font-bold">
            {isElim ? (
              <><Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}</>
            ) : (
              <><Bot className="w-4 h-4 mr-2" /> {t("choose_other_robot")}</>
            )}
          </Button>
          <Button data-testid="btn-back-home" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  if (mode === "eliminatoria") {
    const attemptsLeft = finishData?.attempts_left ?? 0;
    const elimMode = finishData?.elim_mode || "target_time";

    if (elimMode === "wins_vs_losses") {
      // Prefer group-aware payload (`wvl_progress`) when available — it
      // reflects the CURRENT attempt (group of total_games sub-games), not
      // the whole eliminatória.
      const wvl = finishData?.wvl_progress || null;
      const winsCount = wvl?.wins_in_attempt ?? finishData?.wins_count ?? 0;
      const lossesCount = wvl?.losses_in_attempt ?? finishData?.losses_count ?? 0;
      const winsRequired = wvl?.wins_required ?? finishData?.wins_required ?? 3;
      const totalGames = wvl?.total_games ?? finishData?.total_games ?? 5;
      const attemptsLeftWvl = Number(wvl?.attempts_left ?? attemptsLeft ?? 0);
      const gamesPlayed = winsCount + lossesCount;
      const gamesLeft = Math.max(0, totalGames - gamesPlayed);
      // Current ATTEMPT (group) outcome.
      const attemptFailed = !!(wvl && wvl.current_attempt_failed);
      // Group still passable? (remaining games can still take wins to the target)
      const stillPossibleInAttempt = !attemptFailed && (winsCount + gamesLeft) >= winsRequired && gamesLeft > 0;
      // Did the whole eliminatória fail? (attempt failed AND no more attempts)
      const elimFailed = attemptFailed && attemptsLeftWvl === 0;
      return (
        <Modal>
          <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
          <h2
            className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide"
            data-testid="game-lost-title"
          >
            {attemptFailed ? t("wvl_attempt_failed_title") : t("wvl_lost_this_game")}
          </h2>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="wvl-scoreboard-lost">
            <div className="flex flex-col items-center bg-emerald-50 border border-emerald-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">{t("wvl_wins")}</span>
              <span className="font-[Fredoka] text-2xl font-bold text-emerald-700" data-testid="wvl-wins-count-lost">
                {winsCount}
              </span>
            </div>
            <div className="flex flex-col items-center bg-rose-50 border border-rose-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wide">{t("wvl_losses")}</span>
              <span className="font-[Fredoka] text-2xl font-bold text-rose-700" data-testid="wvl-losses-count-lost">
                {lossesCount}
              </span>
            </div>
          </div>
          {stillPossibleInAttempt && (
            <p className="text-slate-600 text-xs md:text-sm mt-3">
              {(() => {
                const need = Math.max(0, winsRequired - winsCount);
                return t(keepGoingKey(gamesLeft, need), { left: gamesLeft, need });
              })()}
            </p>
          )}
          {attemptFailed && !elimFailed && (
            <>
              <p className="text-rose-700 text-xs md:text-sm font-bold mt-3" data-testid="wvl-impossible">
                {t("wvl_impossible")}
              </p>
              <p className="text-slate-700 text-xs md:text-sm mt-2" data-testid="wvl-attempts-left-hint">
                {t("wvl_attempt_failed_hint", { left: attemptsLeftWvl, plural: attemptsLeftWvl === 1 ? "" : "s" })}
              </p>
            </>
          )}
          {elimFailed && (
            <p className="text-rose-700 text-xs md:text-sm font-bold mt-3" data-testid="wvl-no-attempts-left">
              {t("wvl_no_attempts_left")}
            </p>
          )}
          <div className="flex flex-col gap-2 mt-4">
            {stillPossibleInAttempt && (
              <Button
                data-testid="btn-elim-retry"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> {t("wvl_play_next")}
              </Button>
            )}
            {attemptFailed && attemptsLeftWvl > 0 && (
              <Button
                data-testid="btn-elim-new-attempt"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> {t("wvl_start_new_attempt")}
              </Button>
            )}
            <Button
              data-testid="btn-elim-back-lobby"
              onClick={onChooseOtherGame}
              variant="outline"
              className="rounded-full font-bold"
            >
              <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
            </Button>
            <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }

    // ---- AVG-TIME lost — a lost sub-game fails the CURRENT attempt (not the
    // ---- whole eliminatória). If attempts_left > 0 the player can start a
    // ---- fresh attempt of `total_games` sub-games.
    if (elimMode === "avg_time") {
      const avg = finishData?.avg_progress || {};
      const times = Array.isArray(avg.current_attempt_times) ? avg.current_attempt_times : [];
      const targetAvg = Number(avg.target_avg || 0);
      const attemptNumber = Number(avg.attempt_number || 1);
      const attemptsLeftAvg = Number(avg.attempts_left || 0);
      const maxAttempts = Number(avg.max_attempts || 3);
      const totalGames = Number(avg.total_games || 5);
      const gamesPlayedInAttempt = Number(avg.games_played_in_attempt || 0);
      const canRetryAttempt = attemptsLeftAvg > 0;
      return (
        <Modal>
          <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide" data-testid="game-lost-title">
            {t("avg_time_attempt_lost_title")}
          </h2>
          <p className="text-slate-600 text-xs md:text-sm mt-1" data-testid="avg-attempt-header">
            {t("avg_time_attempt_x_of_y", { current: attemptNumber, total: maxAttempts })}
          </p>
          <p className="text-slate-700 text-sm md:text-base mt-2">
            {t("avg_time_attempt_lost_hint")}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center bg-sky-50 border border-sky-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wide">{t("avg_time_games_col")}</span>
              <span className="font-[Fredoka] text-xl font-bold text-sky-700">
                {gamesPlayedInAttempt}<span className="text-sky-500/60 text-base"> / {totalGames}</span>
              </span>
            </div>
            <div className="flex flex-col items-center bg-rose-50 border border-rose-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wide">{t("avg_time_attempts_left_col")}</span>
              <span className="font-[Fredoka] text-xl font-bold text-rose-700">
                {attemptsLeftAvg}<span className="text-rose-500/60 text-base"> / {maxAttempts}</span>
              </span>
            </div>
            <div className="flex flex-col items-center bg-violet-50 border border-violet-200 rounded-xl py-1.5">
              <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wide">{t("avg_time_target")}</span>
              <span className="font-[Fredoka] text-xl font-bold text-violet-700 font-mono">
                {targetAvg.toFixed(0)}s
              </span>
            </div>
          </div>
          {times.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1.5">
              {times.map((s, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-mono font-bold">
                  #{i + 1}: {Number(s).toFixed(1)}s
                </span>
              ))}
            </div>
          )}
          {!canRetryAttempt && (
            <p className="text-rose-700 text-xs md:text-sm mt-3 font-bold" data-testid="avg-no-attempts-left">
              {t("avg_time_no_attempts_left")}
            </p>
          )}
          <div className="flex flex-col gap-2 mt-4">
            {canRetryAttempt && (
              <Button
                data-testid="btn-elim-new-attempt"
                onClick={onPlayAgain}
                disabled={busy}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> {t("avg_time_start_new_attempt")}
              </Button>
            )}
            <Button
              data-testid="btn-elim-back-lobby"
              onClick={onChooseOtherGame}
              variant="outline"
              className="rounded-full font-bold"
            >
              <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
            </Button>
            <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
              <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
            </Button>
          </div>
        </Modal>
      );
    }

    return (
      <Modal>
        <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
        <h2
          className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide"
          data-testid="game-lost-title"
        >
          {t("did_not_pass")}
        </h2>
        <p className="text-slate-700 text-sm md:text-base">{t("game_end")}</p>
        <p className="text-slate-700 text-xs md:text-sm mt-1">
          {t("attempts_remaining_this_elim")}{" "}
          <span className="font-bold text-slate-900" data-testid="elim-attempts-left-lost">{attemptsLeft}</span>
        </p>
        <div className="flex flex-col gap-2 mt-4">
          {attemptsLeft > 0 && (
            <Button
              data-testid="btn-elim-retry"
              onClick={onPlayAgain}
              disabled={busy}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> {t("try_again")}
            </Button>
          )}
          <Button
            data-testid="btn-elim-back-lobby"
            onClick={onChooseOtherGame}
            variant="outline"
            className="rounded-full font-bold"
          >
            <Crown className="w-4 h-4 mr-2" /> {t("back_to_eliminations")}
          </Button>
          <Button data-testid="btn-elim-exit" onClick={onExit} variant="ghost" className="rounded-full text-slate-500">
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal>
      <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto mb-3" />
      <h2
        className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-rose-600 mb-1 tracking-wide"
        data-testid="game-lost-title"
      >
        {mode === "competition" ? t("defeat") : t("wrong_answer")}
      </h2>
      <p className="text-slate-700 text-sm md:text-base">{t("game_end")}</p>
      {mode === "competition" && finishData && (
        <p className="text-slate-700 text-xs md:text-sm mt-1">
          {t("attempts_left")}{" "}
          <span className="font-bold text-slate-900" data-testid="attempts-left-lost">
            {finishData.attempts_available ?? "—"}
          </span>
        </p>
      )}
      {mode === "competition" && history && <ActivitySummary history={history} />}
      {(mode === "training" || mode === "competition") && (
        <FinalRanking gameId={gameId} mode={mode} playerName={playerName} />
      )}
      <div className="flex flex-col gap-2 mt-4">
        {noAttempts && onContinueExhausted ? (
          // Competition — final attempt done. Show a CONTINUAR button so the
          // player can read this modal before the "Tentativas esgotadas"
          // farewell screen appears.
          <Button
            data-testid="btn-continue-exhausted"
            onClick={onContinueExhausted}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold"
          >
            {t("continue_btn")}
          </Button>
        ) : (
          <Button
            data-testid="btn-try-again"
            onClick={onPlayAgain}
            disabled={busy || noAttempts}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> {t("try_again")}
          </Button>
        )}
        {mode !== "competition" && (
          <Button
            data-testid="btn-other-game"
            onClick={onChooseOtherGame}
            variant="outline"
            className="rounded-full font-bold"
          >
            <ListChecks className="w-4 h-4 mr-2" /> {t("choose_other_game")}
          </Button>
        )}
        {!(noAttempts && onContinueExhausted) && (
          <Button
            data-testid="btn-back-home"
            onClick={onExit}
            variant="ghost"
            className="rounded-full text-slate-500"
          >
            {t("exit_lower")}
          </Button>
        )}
      </div>
    </Modal>
  );
}


export function ExhaustedScreen({ history, onExit }) {
  const { t } = useI18n();
  return (
    <Modal>
      <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-3" />
      <h2
        className="text-2xl md:text-3xl font-[Fredoka] font-extrabold text-slate-900 mb-2"
        data-testid="exhausted-title"
      >
        {t("attempts_exhausted")}
      </h2>
      <p className="text-slate-700 text-sm md:text-base">{t("thanks_participation")}</p>
      {history && <ActivitySummary history={history} />}
      <p className="text-xs text-slate-500 mt-3">{t("session_ends_in")}</p>
      <div className="flex flex-col gap-2 mt-3">
        <Button
          data-testid="btn-logout-exhausted"
          onClick={onExit}
          className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-bold"
        >
          <LogOut className="w-4 h-4 mr-2" /> {t("exit_lower")}
        </Button>
      </div>
    </Modal>
  );
}
